/**
 * json-ld.ts -- the ONE safe serializer for embedding schema.org structured data in a
 * `<script type="application/ld+json">` element via dangerouslySetInnerHTML.
 *
 * WHY (adversarial review 2026-07-13, S1 durable fix): `JSON.stringify` does NOT escape
 * `</script>` -- an HTML parser closes a <script> element at the first `</script` REGARDLESS of
 * JS string context, so any untrusted string inside the schema (a question body, an article
 * title, an asset description) could break out of the script element and inject live markup
 * (stored XSS). The WRITER-MINT lane made this reachable for the first time on the open-
 * questions surfaces: LLM-shaped, web-derived free text now flows into `question.question`,
 * which the question detail page embeds in its schema.org Question JSON-LD.
 *
 * THE FIX: escape the SERIALIZED string so the payload is inert inside a <script> element:
 *   <      -> \u003c   (kills `</script>`, `<!--`, and any tag open)
 *   >      -> \u003e   (kills `-->` and tag close; symmetry)
 *   &      -> \u0026   (defense-in-depth for non-script embedding contexts)
 *   U+2028 -> \u2028   (JS line separator -- legal JSON, illegal in pre-ES2019 JS)
 *   U+2029 -> \u2029   (JS paragraph separator -- same)
 * The escaped form is byte-different HTML but parses to the IDENTICAL JSON value (backslash-u
 * is plain JSON string escaping), so search engines / validators see exactly the same
 * structured data.
 *
 * USE THIS for every `<script type="application/ld+json" dangerouslySetInnerHTML=...>` --
 * never a bare `JSON.stringify`.
 */
export function safeJsonLd(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}
